import { PaymentMethod } from "../../../common/enums";
import { type BankAddress } from "../Components/BankValidators/types";
import { type DetailedPaymentPrefUnionObjectType, type RedactedPaymentPref } from "./types";

/**
 * Checks if the BankAddressField object has any non-null and non-empty values.
 *
 * @param {BankAddressField} bankAddress - The bank address object to check.
 * @returns {boolean} - Returns true if any field has a value, otherwise false.
 * @throws {TypeError} - If the input is not a BankAddressField object.
 */
export function hasBankInfo(bankAddress?: BankAddress | null): boolean {
  if (typeof bankAddress !== "object" || bankAddress === null || !bankAddress) {
    return false;
  }

  // We don't care if there are values in region field as there are some edge-cases
  // related to the non-required nature of the BankAddressInput fields where a user
  // can wipe out all values causing weird UI issues
  return Object.entries(bankAddress).some(
    ([key, value]) => key !== "region" && typeof value === "string" && value.trim() !== "",
  );
}

export const getPaymentMethod = (
  paymentPref: DetailedPaymentPrefUnionObjectType | RedactedPaymentPref,
): PaymentMethod => {
  if (!("paymentMethod" in paymentPref)) {
    throw new Error(`Unknown payment method.`);
  }
  // Note that the paymentMethod field is a string, so we convert it to uppercase
  // before comparing it to the enum values to avoid case-sensitivity issues.
  switch (paymentPref.paymentMethod?.toUpperCase()) {
    case PaymentMethod.ACH.toUpperCase():
      return PaymentMethod.ACH;
    case PaymentMethod.Wire.toUpperCase():
      return PaymentMethod.Wire;
    case PaymentMethod.Check.toUpperCase():
      return PaymentMethod.Check;
    default:
      throw new Error(`Unknown payment method: ${paymentPref.paymentMethod}`);
  }
};
